import { createSelector } from '@reduxjs/toolkit';

import type { GlobalState } from '@/redux/store';
import type { FeaturedCategory } from '@/types/FeaturedCategory';

import { LOAD_HOME_DATA_SUCCESS } from '@/redux/modules/home/home.actions';
import { getCanonicalBrowsePathFromHref } from '@/utils/browsePath/getCanonicalBrowsePathFromHref';

import { LOAD_ABOUT_DATA_SUCCESS } from './actions';

// reducer
export type FeaturedCategoriesSlice = {
    categories: FeaturedCategory[];
    featuredCategoriesTitle: string;
};

/**
 * These links are hand-curated rows, so they can point anywhere -- catalogs, browse pages, off-site
 * URLs. Two reasons the guard stays narrowed to `/c/`, both of which break things if it is widened:
 * an off-site href would come back with its origin stripped, and on `/price-guide/{creator}/{id}/`
 * the numeric id is load-bearing -- that page only renders with it.
 */
const canonicalizeBrowseLink = (link: string): string =>
    link.startsWith('/c/') ? getCanonicalBrowsePathFromHref(link) : link;

export const defaultFeaturedCategoriesSlice: FeaturedCategoriesSlice = {
    categories: [],
    featuredCategoriesTitle: '',
};

export default function reducer(
    state: FeaturedCategoriesSlice = defaultFeaturedCategoriesSlice,
    action: any = {}
): FeaturedCategoriesSlice {
    let categories;
    let featuredCategoriesTitle;

    switch (action.type) {
        case LOAD_ABOUT_DATA_SUCCESS:
        case LOAD_HOME_DATA_SUCCESS:
            // categories
            if (action.payload.categories) {
                categories = action.payload.categories.map((x) => {
                    // in the case that we're passed the full url, remove the part we dont need.
                    let link = x.link || '';
                    link = link.replace(/https?:\/\/www.liveauctioneers.com/g, '');
                    // add in the label until the api returns it
                    return {
                        label: x.title,
                        ...x,
                        link: canonicalizeBrowseLink(link),
                    };
                });
            } else {
                categories = [];
            }

            if (action.payload.featuredCategoriesTitle) {
                featuredCategoriesTitle = action.payload.featuredCategoriesTitle;
            }

            return {
                ...state,
                categories,
                featuredCategoriesTitle,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state;
const featuredCategoriesSlice = createSelector(stateSelector, (state) => state.featuredCategories);

export const featuredCategoriesSelector = createSelector(featuredCategoriesSlice, (state) => state.categories);

export const featuredCategoriesTitleSelector = createSelector(
    featuredCategoriesSlice,
    (state) => state.featuredCategoriesTitle
);
